import { UzAlert, UzInput, UzSelect } from '@utazzo/ui';
import { ArrowLeft } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useCreateItineraryFromFixedDeparture,
  useFixedDeparturesList,
} from '../use-fixed-departures';

import { CityPicker } from '@/components/pickers';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useLead } from '@/features/leads/use-leads';

const fmtMoney = (paise: string | null, code: string | null): string => {
  if (paise === null) return '-';
  const amount = Number(paise) / 100;
  return `${code ?? ''} ${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const NIGHTS_9_PLUS = '9plus';

const NIGHT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Any nights' },
  ...[2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({ value: String(n), label: `${n} nights` })),
  { value: NIGHTS_9_PLUS, label: '9+ nights' },
];

/**
 * Picking a fixed departure for a lead (ADR-0069 D2/D3). Pick-only: search,
 * select, and the itinerary is created with defaults so the agent lands on its
 * config page (evolved Configure tab) where tier, dates, party, flights, visa,
 * insurance and supplements are all set - exactly like a FIT itinerary.
 */
export const FixedDeparturePickerPage = (): React.JSX.Element => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const leadQuery = useLead(leadId);
  const lead = leadQuery.data;

  const canSeePrice = useCurrentUser()?.role === 'ADMIN';
  const create = useCreateItineraryFromFixedDeparture(leadId as string);
  const [q, setQ] = useState('');
  const [cityId, setCityId] = useState<string | null>(null);
  // Nights is a fixed ladder, not a free number: templates cluster at 2-9 and
  // anything longer is rare enough to bucket.
  const [nights, setNights] = useState('');
  // Which row is mid-create (for the button spinner). The picker is pick-only:
  // selecting a fixed departure creates the itinerary with defaults and drops
  // the agent on its config page, where all the real setup happens (like FIT).
  const [creatingId, setCreatingId] = useState<string | null>(null);

  const pick = async (fixedDepartureId: string): Promise<void> => {
    if (creatingId) return;
    setCreatingId(fixedDepartureId);
    try {
      const result = await create.mutateAsync({ fixedDepartureId, flightMode: 'NONE' });
      // freshVersion tells the detail page not to auto-bounce to another version
      // while the versions list is still catching up - we want THIS new version.
      navigate(`/itineraries/${result.id}`, { state: { freshVersion: true } });
    } finally {
      setCreatingId(null);
    }
  };

  const list = useFixedDeparturesList({
    page: 1,
    pageSize: 50,
    sortBy: 'updatedAt',
    sortDir: 'desc',
    // Agents only ever see approved templates (ADR-0069 D9).
    sellableOnly: true,
    ...(q.trim().length >= 3 ? { q: q.trim() } : {}),
    ...(cityId ? { cityId } : {}),
    // "9+" is an open-ended bucket, so it filters by a minimum rather than an
    // exact match.
    ...(nights === NIGHTS_9_PLUS ? { minNights: 9 } : {}),
    ...(nights && nights !== NIGHTS_9_PLUS ? { nights: Number(nights) } : {}),
  });

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={() => navigate(`/leads/${leadId}`)}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to lead"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Lead</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {lead?.reference ?? 'Loading...'}
          </span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="text-sm text-zinc-600">Pick a fixed departure</span>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        <section className="rounded-lg border border-zinc-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-zinc-900">Search Fixed Departures</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-[1.6fr_1.2fr_140px]">
            <UzInput
              label="Title or reference"
              placeholder="e.g. Kashmir, or UTZFD2026..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              hint="Three characters or more."
            />
            <div>
              <CityPicker label={undefined} value={cityId} onValueChange={setCityId} />
            </div>
            <UzSelect
              label="Nights"
              value={nights}
              onValueChange={setNights}
              options={NIGHT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </div>
        </section>

        <div className="mt-6 space-y-3">
          {list.isLoading && (
            <div className="h-24 animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60" />
          )}
          {list.data && list.data.data.length === 0 && (
            <UzAlert tone="info">
              No approved fixed departures match. They only appear here once an admin has approved
              them.
            </UzAlert>
          )}
          {list.data?.data.map((row) => (
            <button
              key={row.id}
              type="button"
              disabled={Boolean(creatingId)}
              onClick={() => void pick(row.id)}
              className="flex w-full items-center gap-4 rounded-lg border border-zinc-200 bg-white px-5 py-4 text-left transition-colors hover:border-zinc-300 hover:bg-zinc-50/60 disabled:opacity-60"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-zinc-900">{row.title}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-zinc-500">
                  <span className="font-mono">{row.reference}</span>
                  <span className="text-zinc-300">|</span>
                  <span>
                    {row.numberOfNights}N / {row.numberOfDays}D
                  </span>
                  {row.citySummary && (
                    <>
                      <span className="text-zinc-300">|</span>
                      <span>{row.citySummary}</span>
                    </>
                  )}
                  {row.categorySummary && (
                    <>
                      <span className="text-zinc-300">|</span>
                      <span>{row.categorySummary}</span>
                    </>
                  )}
                </div>
              </div>
              {canSeePrice && (
                <div className="shrink-0 text-right">
                  <div className="text-[10px] tracking-wide text-zinc-400 uppercase">From</div>
                  <div className="text-sm font-semibold text-zinc-900">
                    {fmtMoney(row.startingPricePaise, row.currencyCode)}
                  </div>
                  <div className="text-[10px] text-zinc-400">per person</div>
                </div>
              )}
              <span className="text-primary shrink-0 text-xs font-medium">
                {creatingId === row.id ? 'Creating...' : 'Select ->'}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
